// How each piece of app state maps onto rows of the `records` table.
//   array  → one row per item (row key = item.id)
//   map    → one row per client (row key = client id)
//   single → one row holding the whole value (row key = "_")
export const COLLECTIONS = {
  clients: "array",
  vendors: "array",
  vendorLibrary: "array",
  tasksByClient: "map",
  docsByClient: "map",
  quotesByClient: "map",
  contractsByClient: "map",
  feesByClient: "map",
  reportsByClient: "map",
  // Phase 12
  expensesByClient: "map",
  milestonesByClient: "map",
  dayOfByClient: "map",
  peopleByClient: "map",
  uploadsByClient: "map",
  // Phase 13
  budgetsByClient: "map",
  inspirationByClient: "map",
  portfolio: "array",
  removedClients: "map",
  comparisonSlots: "map",
  customCategories: "single",
  settings: "single"
};

// Business-record keys that get exported to / imported from a backup file (settings stay out of backups).
export const PERSIST_KEYS = ["clients", "tasksByClient", "docsByClient", "vendors", "customCategories", "comparisonSlots", "vendorLibrary", "quotesByClient", "contractsByClient", "feesByClient", "reportsByClient", "expensesByClient", "milestonesByClient", "dayOfByClient", "peopleByClient", "uploadsByClient", "budgetsByClient", "inspirationByClient", "portfolio"];

export function emptyFor(key) {
  const kind = COLLECTIONS[key];
  if (kind === "map") return {};
  if (kind === "array") return [];
  if (key === "customCategories") return [];
  return null;
}

function clientIdFor(key, recKey, item) {
  if (key === "clients") return recKey;
  if (key === "vendors") return item && item.client ? String(item.client) : null;
  if (COLLECTIONS[key] === "map") return recKey;
  return null;
}

// value → [{ recKey, clientId, data, position }]
export function decompose(key, value) {
  const kind = COLLECTIONS[key];
  if (!kind) throw new Error("Unknown collection: " + key);
  if (kind === "array") {
    const seen = new Set();
    return (Array.isArray(value) ? value : []).filter(item => {
      if (!item || item.id == null) return false;
      const k = String(item.id);
      if (seen.has(k)) return false; // duplicate ids would collide on the primary key
      seen.add(k);
      return true;
    }).map((item, i) => ({ recKey: String(item.id), clientId: clientIdFor(key, String(item.id), item), data: item, position: i }));
  }
  if (kind === "map") {
    return Object.entries(value && typeof value === "object" ? value : {}).map(([k, v], i) => ({ recKey: k, clientId: clientIdFor(key, k, v), data: v, position: i }));
  }
  return value == null ? [] : [{ recKey: "_", clientId: null, data: value, position: 0 }];
}

// rows → value
export function compose(key, rows) {
  const kind = COLLECTIONS[key];
  const sorted = (rows || []).slice().sort((a, b) => (a.position - b.position) || (a.recKey < b.recKey ? -1 : 1));
  if (kind === "array") return sorted.map(r => r.data);
  if (kind === "map") { const o = {}; sorted.forEach(r => { o[r.recKey] = r.data; }); return o; }
  return sorted.length ? sorted[0].data : emptyFor(key);
}

export const rowSignature = r => r.position + "|" + JSON.stringify(r.data);

// Compares local rows with the last-known server rows. Returns what to write.
export function diffRows(rows, syncedSigs) {
  const upserts = [];
  const seen = new Set();
  rows.forEach(r => {
    seen.add(r.recKey);
    if (syncedSigs.get(r.recKey) !== rowSignature(r)) upserts.push(r);
  });
  const deletes = [];
  syncedSigs.forEach((_, k) => { if (!seen.has(k)) deletes.push(k); });
  return { upserts, deletes };
}

// Applies one remote row change to a local value.
export function applyRemote(key, value, recKey, data, position, deleted) {
  const kind = COLLECTIONS[key];
  if (kind === "array") {
    const list = Array.isArray(value) ? value.slice() : [];
    const i = list.findIndex(x => x && String(x.id) === recKey);
    if (deleted) { if (i >= 0) list.splice(i, 1); return list; }
    if (i >= 0) { list[i] = data; return list; }
    list.splice(Math.min(position == null ? list.length : position, list.length), 0, data);
    return list;
  }
  if (kind === "map") {
    const o = { ...(value || {}) };
    if (deleted) delete o[recKey]; else o[recKey] = data;
    return o;
  }
  return deleted ? emptyFor(key) : data;
}
